import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Star, Quote, ChevronLeft, ChevronRight, Play, X, ArrowRight } from "lucide-react";
import { Chapter, Reveal } from "../components/Reveal";
import { MM_PHOTO, MM_LOGO, MM_BG, VV_BG, MM_VIDEO } from "../lib/site";
import { useLang } from "../lib/i18n";

export const MM_QUOTE =
  "Sinds onze samenwerking met 4thscenery heeft Mountain Men mooie stappen kunnen zetten in onze groei. 4thscenery heeft een groot deel van onze social media en marketing uit handen genomen, waardoor wij ons volledig konden focussen op onze eigen werkzaamheden en de verdere ontwikkeling van Mountain Men. Ze denken strategisch met ons mee, begrijpen wat ons merk nodig heeft om sterker zichtbaar te worden en weten ideeën om te zetten in concrete acties. Dankzij hun betrokkenheid, creativiteit en persoonlijke aanpak hebben we onze online aanwezigheid flink versterkt, nieuwe doelgroepen bereikt en meer groei kunnen realiseren vanuit social media en marketing. 4thscenery voelt voor ons niet als een externe partij, maar als een partner die echt met ons meedenkt en met ons wil meegroeien. Wij zijn ontzettend tevreden over de samenwerking en kijken uit naar de volgende stappen samen!";

export const VV_QUOTE =
  "Wat we vooral waarderen aan 4thscenery is dat ze niet zomaar losse marketing uitvoeren, maar echt kijken naar wat bij Verjong Vastgoed past. Onze dienstverlening draait om vertrouwen, duidelijkheid en kwaliteit, en dat weten zij goed te vertalen naar onze online uitstraling. Door hun aanpak presenteren we Verjong Vastgoed professioneler en consistenter naar buiten toe.\n\nDe samenwerking verloopt prettig en persoonlijk. Wij kunnen ons richten op vastgoedonderhoud en onze klanten, terwijl 4thscenery ervoor zorgt dat ons verhaal online op de juiste manier wordt verteld. Dat maakt hen voor ons een waardevolle partner.";

const TESTIMONIALS = [
  {
    id: "mountain-men",
    name: "Mountain Men",
    url: null,
    quote: MM_QUOTE,
    logo: { src: MM_LOGO, alt: "Mountain Men logo", framed: true },
    photos: [{ src: MM_PHOTO, alt: "Het team van Mountain Men in de bergen", zoom: true }],
    video: MM_VIDEO,
    bg: MM_BG,
  },
  {
    id: "verjong-vastgoed",
    name: "Verjong Vastgoed",
    url: "https://verjongvastgoed.nl/",
    quote: VV_QUOTE,
    logo: { src: "/media/vv-logo.png", alt: "Verjong Vastgoed logo", framed: false },
    photos: [
      { src: "/media/vv-1.jpg", alt: "Het team van Verjong Vastgoed" },
      { src: "/media/vv-2.jpg", alt: "Verjong Vastgoed · trots op het werk" },
      { src: "/media/vv-3.jpg", alt: "De bus van Verjong Vastgoed" },
      { src: "/media/vv-4.jpg", alt: "Verjong Vastgoed aan het werk" },
      { src: "/media/vv-5.jpg", alt: "Verjong Vastgoed · vakmanschap" },
    ],
    video: null,
    bg: VV_BG,
  },
];

const slide = {
  enter: (d) => ({ opacity: 0, x: d * 48 }),
  center: { opacity: 1, x: 0 },
  exit: (d) => ({ opacity: 0, x: d * -48 }),
};
const EASE = [0.22, 1, 0.36, 1];

const ArrowBtn = ({ onClick, side, testid, label, className = "" }) => (
  <button
    onClick={onClick}
    aria-label={label}
    data-testid={testid}
    className={`group flex h-12 w-12 items-center justify-center rounded-full border border-edge/70 bg-white/60 text-cream backdrop-blur-md transition-colors duration-300 hover:border-amber hover:bg-amber hover:text-[#181510] ${className}`}
  >
    {side === "left" ? (
      <ChevronLeft size={20} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
    ) : (
      <ChevronRight size={20} className="transition-transform duration-300 group-hover:translate-x-0.5" />
    )}
  </button>
);

export const Testimonial = () => {
  const { t, lang } = useLang();
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [light, setLight] = useState(null);

  const active = TESTIMONIALS[index];
  const isNL = lang === "nl";

  const goTo = (next, d) => {
    setDir(d);
    setIndex(((next % TESTIMONIALS.length) + TESTIMONIALS.length) % TESTIMONIALS.length);
    setPhotoIdx(0);
    setShowVideo(false);
    setExpanded(false);
  };
  const next = () => goTo(index + 1, 1);
  const prev = () => goTo(index - 1, -1);

  useEffect(() => {
    if (light === null) return;
    document.documentElement.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setLight(null);
      if (e.key === "ArrowRight") setLight((v) => (v + 1) % active.photos.length);
      if (e.key === "ArrowLeft") setLight((v) => (v - 1 + active.photos.length) % active.photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.documentElement.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [light, active.photos.length]);

  const identity = (
    <div className="flex items-center gap-3">
      {active.logo.framed ? (
        <img
          src={active.logo.src}
          alt={active.logo.alt}
          className="h-12 w-12 rounded-[8px] border border-white/30 object-cover"
          data-testid="testimonial-logo"
        />
      ) : (
        <span className="flex items-center rounded-[8px] border border-white/30 bg-white/95 px-2.5 py-1.5 shadow-sm">
          <img src={active.logo.src} alt={active.logo.alt} className="h-9 w-auto" data-testid="testimonial-logo" />
        </span>
      )}
      <div>
        <p className={`font-archivo text-base font-extrabold text-white ${active.url ? "underline-offset-4 group-hover:underline" : ""}`} data-testid="testimonial-client-name">
          {active.name}
        </p>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">{t.clientOf}</p>
      </div>
    </div>
  );

  return (
  <section id="klanten" data-testid="testimonial-section" className="relative overflow-hidden bg-[#F7F4EC] py-24 md:py-28">
    <div className="absolute inset-0" aria-hidden="true" data-testid="testimonial-mountain-bg">
      <AnimatePresence initial={false}>
        <motion.img
          key={active.id}
          src={active.bg}
          alt=""
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="kenburns absolute inset-0 h-full w-full object-cover"
          data-testid="testimonial-bg-image"
        />
      </AnimatePresence>
      <div className="absolute inset-0 bg-white/65" />
      <div className="absolute inset-0 bg-gradient-to-b from-white/45 via-white/15 to-white/60" />
    </div>
    <div className="relative mx-auto max-w-7xl px-6 md:px-12 lg:px-20">
      <Chapter number="03" label={t.chTestimonial} testid="testimonial-chapter" />

      <ArrowBtn onClick={prev} side="left" testid="testimonial-prev" label={isNL ? "Vorige testimonial" : "Previous testimonial"} className="absolute -left-2 top-1/2 z-20 hidden -translate-y-1/2 xl:flex" />
      <ArrowBtn onClick={next} side="right" testid="testimonial-next" label={isNL ? "Volgende testimonial" : "Next testimonial"} className="absolute -right-2 top-1/2 z-20 hidden -translate-y-1/2 xl:flex" />

      <div className="mt-12 grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
        <Reveal className="relative lg:col-span-5">
          <div className="absolute -left-3 -top-3 h-full w-full rounded-[12px] border border-edge" aria-hidden="true" />
          <AnimatePresence mode="wait" custom={dir} initial={false}>
            <motion.div
              key={active.id + "-media"}
              custom={dir}
              variants={slide}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.4, ease: EASE }}
            >
              <div className="relative overflow-hidden rounded-[12px] border border-edge/70 shadow-xl shadow-black/15" data-testid="testimonial-photo-frame">
                <AnimatePresence mode="wait" initial={false}>
                  {active.video && showVideo ? (
                    <motion.div
                      key="video"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.35 }}
                    >
                      <video
                        src={active.video}
                        controls
                        autoPlay
                        playsInline
                        className="h-[420px] w-full bg-black object-contain md:h-[480px]"
                        data-testid="testimonial-video"
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key={"photo-" + photoIdx}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.35 }}
                      className="relative"
                    >
                      <button
                        onClick={() => setLight(photoIdx)}
                        className="block w-full cursor-zoom-in"
                        data-testid="testimonial-photo-open"
                        aria-label={isNL ? "Open foto in lightbox" : "Open photo in lightbox"}
                      >
                        <img
                          src={active.photos[photoIdx].src}
                          alt={active.photos[photoIdx].alt}
                          className={`h-[420px] w-full object-cover object-center md:h-[480px] ${active.photos[photoIdx].zoom ? "scale-125" : ""}`}
                          data-testid="testimonial-photo"
                        />
                      </button>
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute bottom-5 left-5">
                        {active.url ? (
                          <a href={active.url} target="_blank" rel="noopener noreferrer" className="group block" data-testid="testimonial-client-link">
                            {identity}
                          </a>
                        ) : (
                          identity
                        )}
                      </div>
                      {active.photos.length > 1 && (
                        <div className="absolute bottom-4 right-4 z-10 flex items-center gap-2" data-testid="testimonial-photo-dots">
                          {active.photos.map((_, i) => (
                            <button
                              key={i}
                              onClick={() => setPhotoIdx(i)}
                              aria-label={`${isNL ? "Toon foto" : "Show photo"} ${i + 1}`}
                              className={`h-1.5 w-1.5 rounded-full transition-colors duration-300 ${i === photoIdx ? "bg-amber" : "bg-white/40 hover:bg-white/70"}`}
                              data-testid={`testimonial-photo-dot-${i}`}
                            />
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {!active.video && active.photos.length > 1 && (
                  <>
                    {photoIdx > 0 && (
                      <button
                        onClick={() => setPhotoIdx((v) => (v - 1 + active.photos.length) % active.photos.length)}
                        className="absolute -translate-y-1/2 top-1/2 left-3 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-black/50 text-white backdrop-blur-md transition-colors duration-300 hover:bg-amber hover:text-[#181510] hover:border-amber"
                        data-testid="testimonial-photo-prev"
                        aria-label={isNL ? "Vorige foto" : "Previous photo"}
                      >
                        <ChevronLeft size={18} />
                      </button>
                    )}
                    <button
                      onClick={() => setPhotoIdx((v) => (v + 1) % active.photos.length)}
                      className="absolute -translate-y-1/2 top-1/2 right-3 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-black/50 text-white backdrop-blur-md transition-colors duration-300 hover:bg-amber hover:text-[#181510] hover:border-amber"
                      data-testid="testimonial-photo-next"
                      aria-label={isNL ? "Volgende foto" : "Next photo"}
                    >
                      <ChevronRight size={18} />
                    </button>
                  </>
                )}

                {active.video && (
                  <>
                    <button
                      onClick={() => setShowVideo((v) => !v)}
                      className="absolute -translate-y-1/2 top-1/2 right-3 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-black/50 text-white backdrop-blur-md transition-colors duration-300 hover:bg-amber hover:text-[#181510] hover:border-amber"
                      data-testid="testimonial-media-toggle"
                      aria-label={showVideo ? (isNL ? "Toon foto" : "Show photo") : (isNL ? "Bekijk video" : "Watch video")}
                    >
                      {showVideo ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
                    </button>
                    <div className="absolute bottom-4 right-4 z-10 flex items-center gap-2" data-testid="testimonial-media-dots">
                      <span className={`h-1.5 w-1.5 rounded-full transition-colors ${showVideo ? "bg-white/40" : "bg-amber"}`} />
                      <span className={`h-1.5 w-1.5 rounded-full transition-colors ${showVideo ? "bg-amber" : "bg-white/40"}`} />
                    </div>
                    {!showVideo && (
                      <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-full bg-black/50 px-3 py-1.5 backdrop-blur-md" data-testid="testimonial-video-hint">
                        <Play size={11} className="fill-amber text-amber" />
                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/90">Video</span>
                      </div>
                    )}
                  </>
                )}
              </div>

            </motion.div>
          </AnimatePresence>
        </Reveal>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <Quote size={34} className="rotate-180 text-amber" aria-hidden="true" />
          </Reveal>
          <AnimatePresence mode="wait" custom={dir} initial={false}>
            <motion.div
              key={active.id + "-text"}
              custom={dir}
              variants={slide}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.4, delay: 0.06, ease: EASE }}
            >
              <blockquote
                className={`mt-6 whitespace-pre-wrap text-sm leading-relaxed text-cream md:text-lg ${expanded ? "" : "line-clamp-4 md:line-clamp-none"}`}
                data-testid="testimonial-quote"
              >
                "{active.quote}"
              </blockquote>
              <button
                onClick={() => setExpanded((v) => !v)}
                className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-amberdeep transition-colors hover:text-amber md:hidden"
                data-testid="testimonial-read-more"
              >
                {expanded ? (isNL ? "Lees minder" : "Read less") : (isNL ? "Lees verder" : "Read more")}
              </button>
              <div className="mt-8 flex flex-wrap items-center gap-5">
                <div className="flex items-center gap-1" data-testid="testimonial-stars" aria-label={t.starsLabel}>
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="flex h-7 w-7 items-center justify-center rounded-[3px] bg-[#00b67a]">
                      <Star size={15} className="fill-white text-white" />
                    </span>
                  ))}
                </div>
                <span className="font-archivo text-sm font-bold text-cream" data-testid="testimonial-author">{active.name}</span>
                <Link
                  to={`/cases/${active.id}`}
                  className="group inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-amberdeep transition-colors hover:text-amber"
                  data-testid="testimonial-case-link"
                >
                  {isNL ? "Bekijk het project" : "View the project"}
                  <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="mt-10 flex items-center justify-center gap-6 xl:hidden" data-testid="testimonial-mobile-nav">
        <ArrowBtn onClick={prev} side="left" testid="testimonial-prev-mobile" label={isNL ? "Vorige testimonial" : "Previous testimonial"} className="h-11 w-11" />
        <div className="flex items-center gap-2" data-testid="testimonial-dots">
          {TESTIMONIALS.map((item, i) => (
            <button
              key={item.id}
              onClick={() => i !== index && goTo(i, i > index ? 1 : -1)}
              aria-label={item.name}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-amber" : "w-1.5 bg-cream/25 hover:bg-cream/50"}`}
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>
        <ArrowBtn onClick={next} side="right" testid="testimonial-next-mobile" label={isNL ? "Volgende testimonial" : "Next testimonial"} className="h-11 w-11" />
      </div>
    </div>

    <AnimatePresence>
      {light !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
          onClick={() => setLight(null)}
          data-testid="lightbox"
        >
          <button
            onClick={() => setLight(null)}
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur-md transition-colors duration-300 hover:bg-amber hover:text-[#181510]"
            data-testid="lightbox-close"
            aria-label={isNL ? "Sluit lightbox" : "Close lightbox"}
          >
            <X size={18} />
          </button>
          {active.photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLight((v) => (v - 1 + active.photos.length) % active.photos.length); }}
                className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur-md transition-colors duration-300 hover:bg-amber hover:text-[#181510] md:left-6"
                data-testid="lightbox-prev"
                aria-label={isNL ? "Vorige foto" : "Previous photo"}
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setLight((v) => (v + 1) % active.photos.length); }}
                className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur-md transition-colors duration-300 hover:bg-amber hover:text-[#181510] md:right-6"
                data-testid="lightbox-next"
                aria-label={isNL ? "Volgende foto" : "Next photo"}
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}
          <motion.img
            key={light}
            src={active.photos[light].src}
            alt={active.photos[light].alt}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[92vw] rounded-[10px] object-contain shadow-2xl"
            data-testid="lightbox-image"
          />
          <p className="absolute bottom-5 left-1/2 -translate-x-1/2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/60" data-testid="lightbox-counter">
            {light + 1} / {active.photos.length}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </section>
  );
};
